import { CloseRounded, KeyboardArrowDown } from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { useState } from "react";

import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import DocTypePickerModal from "./DocTypePickerModal";
import { teal } from "../../../theme/colors";

// Input-shaped trigger for the doc-type picker modal. Reads like a select
// (shows the assigned type, with a short group tag), but clicking opens the
// full-screen browser instead of a dropdown.
//
// Drop-in for SelectFileAssignmentInput's { docGroup, docType, onChange }
// contract — onChange fires with { docGroup, docType } exactly the same way, so
// the caller's group + type both update on select.
//
// Trial usage only: currently wired into the document-upload bulk-edit panel.

const ACCENT = teal[500]; // brand surface — rings, borders, fills
const ACCENT_INK = teal[700]; // active/selected text
const LINE = "#E2E8F1";
const MUTED = "#79879B";
const MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

// "Closed Loan Package Scans" → "CLOSED". Enough to disambiguate two types
// with the same name in different groups without eating the row.
const shortGroupTag = (group) =>
  String(group || "")
    .split(/[\s&]+/)[0]
    .toUpperCase()
    .slice(0, 10);

// Heights are matched to the rendered height of the outlined TextFields this
// app actually puts in a row — which is NOT MUI's nominal 40/56, because the
// dense forms here override the input font-size to 13px and that shrinks the
// control. A `size="small"` TextField with a 13px input measures ~36px
// (8.5px padding × 2 + 1.4375em line box), so that's what `small` is here.
//   xs 30 · small 36 (dense rows) · medium 40 · large 56 (stock MUI default)
const SIZES = {
  xs: { minHeight: 30, padding: "3px 8px", font: 12, tag: 9.5, caret: 15 },
  small: { minHeight: 36, padding: "4px 10px", font: 13, tag: 10.5, caret: 17 },
  medium: { minHeight: 40, padding: "6px 12px", font: 13.5, tag: 11, caret: 18 },
  large: { minHeight: 56, padding: "12px 14px", font: 15, tag: 12, caret: 20 },
};

const DocTypePickerField = ({
  docGroup,
  docType,
  onChange,
  placeholder = "Select document type",
  subjectLabel,
  suggestFrom,
  allowUnassigned = true,
  allowClear = true,
  size = "medium",
  disabled = false,
  sx,
}) => {
  const [open, setOpen] = useState(false);

  const filled = !!docGroup;
  const displayValue = docType || (docGroup ? "Unassigned type" : "");
  const dims = SIZES[size] || SIZES.medium;

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          minWidth: 0,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.6 : 1,
          border: `1px solid ${LINE}`,
          borderRadius: "9px",
          background: "#fff",
          p: dims.padding,
          minHeight: dims.minHeight,
          transition: "border-color 120ms, background 120ms",
          "&:hover": {
            borderColor: "#C9D3E2",
            background: "#FCFDFF",
          },
          "&:focus-visible": {
            outline: `2px solid ${ACCENT}`,
            outlineOffset: "2px",
          },
          ...sx,
        }}
      >
        {filled ? (
          <Flx ac g={1} sx={{ minWidth: 0 }}>
            <Tooltip title={docGroup} placement="top">
              <Txt
                sx={{
                  fontFamily: MONO,
                  fontSize: dims.tag,
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  color: ACCENT_INK,
                  background: "#E7F4F8",
                  borderRadius: "4px",
                  p: "2px 5px",
                  flex: "none",
                }}
              >
                {shortGroupTag(docGroup)}
              </Txt>
            </Tooltip>
            <Txt
              sx={{
                fontSize: dims.font,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontStyle: docType ? "normal" : "italic",
              }}
            >
              {displayValue}
            </Txt>
          </Flx>
        ) : (
          <Txt sx={{ fontSize: dims.font, color: MUTED }}>{placeholder}</Txt>
        )}

        {allowClear && filled && !disabled && (
          <Box
            component="span"
            role="button"
            aria-label="Clear document type"
            onClick={(e) => {
              e.stopPropagation();
              onChange?.({ docGroup: null, docType: null });
            }}
            sx={{
              ml: "auto",
              display: "flex",
              color: MUTED,
              borderRadius: "50%",
              p: "2px",
              "&:hover": { color: "#0E1626", background: "#ffffffcc" },
            }}
          >
            <CloseRounded sx={{ fontSize: 14 }} />
          </Box>
        )}
        <KeyboardArrowDown
          sx={{
            ml: allowClear && filled ? 0 : "auto",
            fontSize: dims.caret,
            color: MUTED,
            flex: "none",
          }}
        />
      </Box>

      <DocTypePickerModal
        open={open}
        onClose={() => setOpen(false)}
        onChange={onChange}
        docGroup={docGroup}
        docType={docType}
        subjectLabel={subjectLabel}
        suggestFrom={suggestFrom}
        allowUnassigned={allowUnassigned}
      />
    </>
  );
};

export default DocTypePickerField;
